import React from "react";

/**
 * AppIcon — the canonical Find2near premium 3D icon component.
 *
 * Usage:  <AppIcon name="hotels" size={64} />
 *
 * • Sources PNGs from `/public/icons/find2near-icon-<name>.png`
 * • Alias map maps Home-grid category ids (singular) to registry names.
 * • Falls back to a supplied Lucide element (`fallback` prop) when the
 *   PNG hasn't been generated yet.
 *
 * Slice 23d — full 50-icon family live (Hotels master anchor, transparent
 * PNG pipeline).
 */
export const APP_ICON_NAMES = [
  // Core 15
  "home", "search", "map", "messenger", "notifications", "me",
  "wallet", "findpay", "business", "marketplace", "matrimonial",
  "social", "emergency", "rideshare", "cart",
  // Extended 15
  "hotels", "flights", "trains", "cabs", "movies", "property",
  "jobs", "locker", "health", "insurance", "recharges", "charity",
  "astrology", "delivery", "reviews",
  // Full expansion 20
  "schools", "shopping", "services", "food", "stores", "vacation",
  "tutors", "baby_care", "elder_care", "pet_care", "buses", "weddings",
  "party_halls", "salon", "gym", "doctors", "hospitals", "micropay",
  "reels", "community",
  // Live World 10
  "gold", "silver", "currency", "commodity", "crypto", "vehicle",
  "auction", "transport", "podcaster", "wfc",
];

export const APP_ICON_ALIASES = {
  // Home grid singular → registry plural
  hotel:        "hotels",
  cab:          "cabs",
  taxi:         "cabs",
  car:          "cabs",
  car_service:  "cabs",
  bus:          "buses",
  flight:       "flights",
  train:        "trains",
  school:       "schools",
  tutor:        "tutors",
  doctor:       "doctors",
  hospital:     "hospitals",
  clinic:       "hospitals",
  pharmacy:     "hospitals",
  medical:      "doctors",
  movie:        "movies",
  cinema:       "movies",
  entertainment: "movies",
  service:      "services",
  restaurant:   "food",
  store:        "stores",
  product:      "shopping",
  wedding:      "weddings",
  marriage_hall: "weddings",
  party_hall:   "party_halls",
  reel:         "reels",
  // Meaningful synonyms
  ride:         "rideshare",
  ride_share:   "rideshare",
  shop:         "shopping",
  job:          "jobs",
  vacancy:      "jobs",
  property_house: "property",
  house:        "property",
  real_estate:  "property",
  chat:         "messenger",
  messages:     "messenger",
  message:      "messenger",
  inbox:        "messenger",
  notification: "notifications",
  bell:         "notifications",
  profile:      "me",
  user:         "me",
  account:      "me",
  pay:          "findpay",
  payment:      "findpay",
  cards:        "findpay",
  gift:         "charity",
  donation:     "charity",
  review:       "reviews",
  star:         "reviews",
  location:     "map",
  navigation:   "map",
  nearby:       "map",
  discover:     "map",
  file:         "locker",
  files:        "locker",
  document:     "locker",
  documents:    "locker",
  policy:       "insurance",
  recharge:     "recharges",
  bill:         "recharges",
  bills:        "recharges",
  fitness:      "gym",
  spa:          "salon",
  beauty:       "salon",
  baby:         "baby_care",
  babycare:     "baby_care",
  elder:        "elder_care",
  eldercare:    "elder_care",
  pet:          "pet_care",
  petcare:      "pet_care",
  friends:      "community",
  group:        "community",
  // Home-grid ids that map obviously to existing family members
  donate:       "charity",
  foundation:   "charity",
  welfare:      "charity",
  learn:        "tutors",
  brands:       "business",
};

export const canonicalIconName = (name) => {
  if (!name) return null;
  const lower = String(name).toLowerCase();
  if (APP_ICON_NAMES.includes(lower)) return lower;
  const target = APP_ICON_ALIASES[lower];
  if (target && APP_ICON_NAMES.includes(target)) return target;
  return null;
};

export const hasAppIcon = (name) => Boolean(canonicalIconName(name));

const AppIcon = ({ name, size = 48, className = "", alt, fallback = null, ...rest }) => {
  const canonical = canonicalIconName(name);
  if (!canonical) return fallback;
  const src = `/icons/find2near-icon-${canonical}.png`;
  return (
    <img
      src={src}
      alt={alt || canonical}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      className={`inline-block object-contain select-none pointer-events-none ${className}`.trim()}
      data-testid={`app-icon-${canonical}`}
      {...rest}
    />
  );
};

export default AppIcon;
export { AppIcon };
